import React, { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

const COLOR_MAP = {
  "en ligne": "#10B981",
  cheque: "#6366F1",
  chèque: "#6366F1",
  cb: "#06B6D4",
  especes: "#F59E0B",
  espèces: "#F59E0B",
  virement: "#0EA5E9",
  autre: "#A855F7",
  "non renseigne": "#94A3B8",
  "non renseigné": "#94A3B8",
};

const FALLBACK_COLORS = [
  "#22C55E",
  "#EAB308",
  "#F97316",
  "#EF4444",
  "#0EA5E9",
  "#A855F7",
  "#14B8A6",
  "#84CC16",
];

function normalize(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function colorFor(label, index) {
  return COLOR_MAP[normalize(label)] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function TooltipContent({ active, payload }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-bold text-slate-900 dark:text-white">{row?.name}</p>
      <p className="mt-2 text-slate-700 dark:text-slate-300">
        Paiements : <span className="font-bold">{formatNumber(row?.count)}</span>
      </p>
      <p className="mt-1 text-xs font-bold text-slate-500 dark:text-slate-400">
        {row?.value}%
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid gap-4 xl:grid-cols-[360px_1fr]">
      <div className="h-[300px] animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
      <div className="h-[300px] animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

export default function PaymentTypeChart({ data = [], loading = false }) {
  const prepared = useMemo(() => {
    return (Array.isArray(data) ? data : [])
      .filter((item) => item?.type)
      .map((item, index) => ({
        name: item.type,
        value: Number(item.percent || 0),
        count: Number(item.count || 0),
        color: colorFor(item.type, index),
      }));
  }, [data]);

  const totalCount = prepared.reduce((sum, item) => sum + item.count, 0);

  if (loading) return <LoadingSkeleton />;

  if (!prepared.length) {
    return (
      <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950/50">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Aucune donnée de type de paiement à afficher.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[360px_1fr]">
      <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={prepared}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="58%"
                outerRadius="82%"
                paddingAngle={2}
              >
                {prepared.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>

              <Tooltip content={<TooltipContent />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Paiements analysés
          </p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {formatNumber(totalCount)}
          </p>
        </div>
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/60">
        <div className="space-y-2">
          {prepared.map((item) => (
            <div
              key={item.name}
              className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-center gap-3">
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ background: item.color }}
                />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                      {item.name}
                    </p>

                    <p className="shrink-0 text-sm font-bold text-slate-900 dark:text-white">
                      {item.count}
                    </p>
                  </div>

                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.max(0, Math.min(100, item.value))}%`,
                        background: item.color,
                      }}
                    />
                  </div>
                </div>

                <span className="w-12 shrink-0 text-right text-xs font-bold text-slate-500 dark:text-slate-400">
                  {item.value}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}